<template>
  <component
    v-bind="linkProps"
    :is="component"
    data-slot="empty-screen"
    :class="[
      'empty-screen-component glass-panel relative isolate flex min-h-[220px] flex-col overflow-hidden rounded-xl text-sm no-underline',
      !mobile && 'sm:flex-row sm:items-center',
      isLink && 'cursor-pointer transition-shadow hover:ring-2 hover:ring-primary/40',
    ]"
  >
    <span
      v-if="colorful"
      :class="[
        'absolute top-0 left-0 -z-10 aspect-square w-full rotate-180 bg-[length:160%] bg-[position:20%_0%] bg-no-repeat',
        !mobile && 'sm:top-1/2 sm:-left-[10%] sm:w-3/4 sm:-translate-y-1/2 sm:-rotate-45 sm:bg-[length:130%] sm:bg-[position:100%_70%]',
      ]"
      :style="{ backgroundImage: 'url(' + toolsGradient + ')' }"
    />
    <span
      v-else
      :class="[
        'absolute -z-10 aspect-square rounded-full bg-primary blur-[100px]',
        mobile ? '-left-1/2 bottom-1/2 w-[200%]' : '-left-1/2 bottom-1/2 w-[200%] sm:inset-y-0 sm:-left-[10%] sm:my-auto sm:w-[55%]',
      ]"
    />

    <div :class="[ 'column left flex items-start gap-4 px-5 pt-5 pb-1', !mobile && 'sm:w-1/2 sm:py-5 sm:pl-[8%]' ]">
      <loader-circle-icon v-if="icon === 'loader'" class="size-12 shrink-0 animate-spin text-white" />
      <i v-else :class="icon" class="icon white size-12 shrink-0" />
      <div class="flex min-w-0 flex-col items-start gap-2.5">
        <h1 class="title m-0 text-xl leading-tight font-bold text-white">
          {{ title }}
        </h1>
        <span v-if="flagText" :class="[ 'flag rounded-full bg-white px-3 py-0.5 text-xs font-semibold', colorful ? 'text-[#9800BC]' : 'text-primary' ]">
          {{ flagText }}
        </span>
      </div>
    </div>

    <div :class="[ 'column right flex flex-col gap-4 px-5 pt-1 pb-5', !mobile && 'sm:w-1/2 sm:py-5 sm:pr-[8%]', isLink && 'pr-14' ]">
      <div class="description text-foreground">
        {{ description }}
      </div>
      <div v-if="$slots['under-description']" class="under-description">
        <slot name="under-description" />
      </div>
    </div>

    <span
      v-if="isLink"
      aria-hidden="true"
      :class="[ 'absolute right-5 flex size-6 items-center justify-center rounded-full bg-card text-primary shadow-sm', mobile ? 'bottom-2.5' : 'bottom-2.5 sm:inset-y-0 sm:my-auto' ]"
    >
      <chevron-right-icon class="size-4" />
    </span>
  </component>
</template>

<script>
import { ChevronRight, LoaderCircle } from '@lucide/vue';

import toolsGradient from '@/assets/img/tools-gradient.png';

/**
 * Promo-style empty state card: title and icon on a primary glow, description
 * (and optional actions) beside it. With `path` (or a click listener) the
 * whole card is a link. `mobile` forces the stacked layout.
 */
export default {
  name: 'EmptyScreen',
  components: {
    ChevronRightIcon: ChevronRight,
    LoaderCircleIcon: LoaderCircle,
  },
  props: {
    // Icon class name from src/assets/icons.css (e.g. "posts", "clock-sleepy"), or "loader".
    icon: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    flagText: {
      type: String,
      required: false,
      default: '',
    },
    description: {
      type: String,
      required: true,
    },
    path: {
      type: String,
      required: false,
      default: '',
    },
    mobile: {
      type: Boolean,
      required: false,
      default: () => false,
    },
    colorful: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  data: () => ({
    toolsGradient,
  }),
  computed: {
    isExternalLink() {
      return this.path.startsWith('http');
    },
    isLink() {
      return !!this.path || !!this.$attrs.onClick;
    },
    component() {
      if (this.path) {
        return this.isExternalLink ? 'a' : 'router-link';
      }

      return 'div';
    },
    linkProps() {
      if (!this.path) {
        return {};
      }

      return this.isExternalLink ? { href: this.path, target: '_blank' } : { to: this.path };
    },
  },
};
</script>
